// frontend/src/services/dashboardService.js - Dashboard API Service
import employeeService from './employeeService';

/**
 * Dashboard API Service
 * Handles all dashboard-related API calls
 */
class DashboardService {
  /**
   * Get comprehensive dashboard data
   * @returns {Promise<Object>} Dashboard data
   */
  async getDashboardData() {
    return await employeeService.getDashboard();
  }

  /**
   * Get today's attendance summary
   * @returns {Promise<Object>} Today's attendance data
   */
  async getTodayAttendance() {
    return await employeeService.getTodayAttendance();
  }

  /**
   * Get attendance summary for a period
   * @param {string} period - Period ('today', 'week', 'month')
   * @returns {Promise<Object>} Attendance summary
   */
  async getAttendanceSummary(period = 'month') {
    return await employeeService.getAttendanceSummary({ period });
  }

  /**
   * Get recent attendance history
   * @param {number} limit - Number of records to fetch
   * @returns {Promise<Object>} Attendance history
   */
  async getRecentAttendance(limit = 5) {
    return await employeeService.getAttendanceHistory({ limit });
  }

  /**
   * Get leave balance information
   * @param {number} year - Year to get balance for
   * @returns {Promise<Object>} Leave balance data
   */
  async getLeaveBalance(year = new Date().getFullYear()) {
    return await employeeService.getLeaveBalance({ year });
  }

  /**
   * Get employee notifications
   * @param {number} limit - Number of notifications to fetch
   * @returns {Promise<Object>} Notifications data
   */
  async getNotifications(limit = 10) {
    try {
      return await employeeService.getNotifications({ limit });
    } catch (error) {
      // If notifications endpoint doesn't exist yet, return empty array
      if (error.response?.status === 404 || error.response?.status === 501) {
        return {
          success: true,
          data: { notifications: [] }
        };
      }
      throw error;
    }
  }

  /**
   * Mark notification as read
   * @param {number} notificationId - ID of notification to mark as read
   * @returns {Promise<Object>} Response data
   */
  async markNotificationRead(notificationId) {
    try {
      return await employeeService.markNotificationAsRead(notificationId);
    } catch (error) {
      // If endpoint doesn't exist, return success (for mock data)
      if (error.response?.status === 404 || error.response?.status === 501) {
        return { success: true };
      }
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   * @returns {Promise<Object>} Response data
   */
  async markAllNotificationsRead() {
    try {
      return await employeeService.markAllNotificationsAsRead();
    } catch (error) {
      // If endpoint doesn't exist, return success (for mock data)
      if (error.response?.status === 404 || error.response?.status === 501) {
        return { success: true };
      }
      throw error;
    }
  }

  /**
   * Get weather data for dashboard widget
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {Promise<Object>} Weather data
   */
  async getWeatherData(lat, lng) {
    // This would typically call a weather API
    // For now, return mock data
    return {
      success: true,
      data: {
        weather: {
          location: 'Current Location',
          temperature: 22,
          condition: 'Partly Cloudy',
          humidity: 65,
          windSpeed: '5 km/h'
        }
      }
    };
  }

  /**
   * Get employee profile summary for dashboard
   * @returns {Promise<Object>} Profile summary
   */
  async getProfileSummary() {
    return await employeeService.getProfile();
  }
}

// Export singleton instance
export const dashboardService = new DashboardService();
export default dashboardService;